import { WIDGET_CONTROL_TYPE } from '@examdojo/core/widget';
import type { GraphpadWidgetConfig } from '../graphpad.model';

/**
 * Comprehensive quadratic preset showing both standard form (abc) and vertex form (ahk).
 *
 * f1 (sky): Standard form ax² + bx + c
 *   - Handle for translation
 *   - Roots with coordinates
 *
 * f2 (pink): Vertex form a(x-h)² + k
 *   - Handle for translation
 *   - Extrema (vertex)
 *   - Tangent line
 *   - Secant line with rate triangle
 *   - Derivative curve
 *   - Axis of symmetry
 */
export const QUADRATIC_PRESET: GraphpadWidgetConfig = {
  id: 'quadratic',
  type: 'graphpad',
  params: {
    // Standard form (abc)
    f1: { type: 'quadratic', params: { a: 1, b: 4, c: -3 } },
    'f1-translate': { type: 'handle', params: { of: 'f1', behavior: 'translate' } },
    'f1-roots': { type: 'roots', params: { of: 'f1' } },

    // Vertex form (ahk)
    f2: { type: 'quadratic-vertex', params: { a: 1, h: 4, k: 2 } },
    'f2-translate': { type: 'handle', params: { of: 'f2', behavior: 'translate' } },
    'f2-extrema': { type: 'extrema', params: { of: 'f2' } },
    'f2-axis': { type: 'axisOfSymmetry', params: { of: 'f2' } },

    // Intersection
    'f1-f2-intersect': { type: 'intersection', params: { of: ['f1', 'f2'] } },
  },
  display: {
    canvas: { color: 'neutral-0' },
    coordinateSystem: {
      boundingbox: { xStart: -10, xEnd: 10, yStart: -10, yEnd: 10 },
      grid: { type: 'lines', visible: false },
    },
    elements: {
      // f1 (abc) - sky color
      f1: {
        curve: { color: 'sky' },
      },
      'f1-translate': {
        point: { color: 'sky' },
      },
      'f1-roots': {
        point: {
          color: 'sky',
          label: { contentVariant: 'coord', position: 'bottom', color: 'sky' },
        },
      },

      // f2 (ahk) - pink color
      f2: {
        curve: { color: 'pink' },
      },
      'f2-translate': {
        point: { color: 'pink' },
      },
      'f2-extrema': {
        subparts: {
          vertex: { point: { color: 'pink' } },
          minima: { point: { color: 'pink' } },
          maxima: { point: { color: 'pink' } },
        },
      },
      'f2-axis': { line: { color: 'pink' } },

      // Intersection
      'f1-f2-intersect': { point: { color: 'neutral' } },
    },
  },
  behavior: {
    controls: [
      {
        id: 'slider-f1-a',
        type: WIDGET_CONTROL_TYPE.Slider,
        label: 'a',
        param: 'f1.a',
        range: [-3, 3, 0.1],
        defaultValue: 1,
        editable: true,
        color: 'sky',
      },

      {
        id: 'slider-f2-a',
        type: WIDGET_CONTROL_TYPE.Slider,
        label: 'a',
        param: 'f2.a',
        range: [-3, 3, 0.1],
        defaultValue: 0.5,
        editable: true,
        color: 'pink',
      },
      {
        id: 'slider-f1-b',
        type: WIDGET_CONTROL_TYPE.Slider,
        label: 'b',
        param: 'f1.b',
        range: [-10, 10, 0.5],
        defaultValue: 2,
        editable: true,
        color: 'sky',
      },
      {
        id: 'slider-f2-h',
        type: WIDGET_CONTROL_TYPE.Slider,
        label: 'h',
        param: 'f2.h',
        range: [-8, 8, 0.5],
        defaultValue: 2,
        editable: true,
        color: 'pink',
      },
      {
        id: 'slider-f1-c',
        type: WIDGET_CONTROL_TYPE.Slider,
        label: 'c',
        param: 'f1.c',
        range: [-10, 10, 0.5],
        defaultValue: -3,
        editable: true,
        color: 'sky',
      },
      {
        id: 'slider-f2-k',
        type: WIDGET_CONTROL_TYPE.Slider,
        label: 'k',
        param: 'f2.k',
        range: [-8, 8, 0.5],
        defaultValue: -2,
        editable: true,
        color: 'pink',
      },
    ],
  },
};
